/**
 * The station-volumes chip that sits on an analysis panel: a scope note that
 * says why the corridor's net differs from the panel's own net, and a button
 * that opens the station-volumes dock on this layer.
 *
 * One component, two callers. `SurfaceDifferencePanel` and
 * `DeviationVolumePanel` carried the same note-plus-button block verbatim,
 * differing only in which scope sentence they printed — which meant a copy or
 * a formatting fix landed on one panel and not the other. The `kind` prop is
 * the whole difference now.
 */
import {
  stationBandsPinnedLayerAtom,
  stationBandsVisibleAtom,
  stationVolumeDockAtom,
  stationVolumeDockHeightAtom,
} from '@/atoms/stationVolumes.atom';
import { useBandSource } from '@/components/3D/StationBands';
import { Checkbox } from '@/components/ui/checkbox';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import { useFeature } from '@/hooks/useFeature';
import { formatM3Row } from '@/lib/stationVolumes/format';
import type { StationSummary, StationVolumesAnalysisKind } from '@/lib/stationVolumes/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { StationBandsLegend } from './StationBandsLegend';

export interface StationVolumesChipProps {
  layerId: string;
  /** Decides the scope sentence and the chip's test id. */
  kind: StationVolumesAnalysisKind;
  /** The run's summary block, off the layer's metadata. Cut and fill are read
   *  for the Net's PRECISION, not to print them — see below. */
  summary: Pick<StationSummary, 'cutVolumeM3' | 'fillVolumeM3' | 'netVolumeM3' | 'binCount'>;
}

export const StationVolumesChip = ({ layerId, kind, summary }: StationVolumesChipProps) => {
  const { t } = useTranslation('project');
  const setDock = useSetAtom(stationVolumeDockAtom);
  // Ruling A7: seed the height from the persisted preference, never from
  // DOCK_HEIGHT_DEFAULT — opening the dock must not stomp the user's drag.
  const dockHeight = useAtomValue(stationVolumeDockHeightAtom);
  const deviation = kind === 'deviation';

  // "Show station bands on map" — the pin (`stationBandsPinnedLayerAtom`)
  // that keeps this run's corridor on the 3D scene and the 2D map with the
  // dock closed. It lives here, on the card that already speaks for the run,
  // rather than on a card of its own: the viewer has one floating column for
  // an analysis and a second one would only compete with it.
  const gate = useFeature('analysis.stationVolumes');
  const [pinnedLayerId, setPinnedLayerId] = useAtom(stationBandsPinnedLayerAtom);
  const setBandsVisible = useSetAtom(stationBandsVisibleAtom(layerId));
  const pinned = gate.allowed && pinnedLayerId === layerId;

  return (
    <>
      {/* Two "net" figures sit on the host panel once a station run exists, and
          they legitimately differ: the panel's KPIs cover the whole analysis
          grid, the chip covers the corridor only — and on a deviation run the
          two also carry opposite signs. Unlabelled, a reader takes one of them
          for wrong. */}
      <p className='mt-2 text-[11px] leading-snug text-slate-500 dark:text-stone-400'>
        {t(deviation ? 'stationVolumes.panel.gridScopeNoteDeviation' : 'stationVolumes.panel.gridScopeNote')}
      </p>
      <button
        type='button'
        data-testid={deviation ? 'dev-station-volumes-chip' : 'sd-station-volumes-chip'}
        title={t('stationVolumes.panel.chipOpen')}
        className='mt-2 flex w-full flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 rounded border border-slate-200 px-2 py-1 text-left hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-stone-700 dark:hover:bg-stone-800'
        onClick={() => setDock({ layerId, tab: 'chart', height: dockHeight })}>
        <span className='font-semibold'>{t('stationVolumes.panel.chipTitle')}</span>
        <span className='tabular-nums text-slate-600 dark:text-stone-300'>
          {/* `formatM3Row`, not `formatM3` and certainly not `toFixed(0)`: the
              chip and the dock's hero Net are the same figure, so they have to
              be the same STRING. The dock pools its decimals over the whole
              Cut / Fill / Net row (`StationVolumeDock`), which is why the row
              is passed here and only its net printed — the chip alone would
              give a −49.5 m³ net beside a 4 200 m³ cut three decimals of
              accuracy the row does not claim. */}
          {t('stationVolumes.panel.chip', {
            cut: formatM3Row([summary.cutVolumeM3, summary.fillVolumeM3])[0],
            fill: formatM3Row([summary.cutVolumeM3, summary.fillVolumeM3])[1],
            count: summary.binCount,
            formatted: summary.binCount.toLocaleString(),
          })}
        </span>
      </button>
      {/* Gated, never `disabled` (CLAUDE.md): a locked click opens the
          upgrade dialog. */}
      <label
        className={`mt-2 flex items-center gap-2 ${gate.allowed ? '' : 'opacity-60'}`}
        title={t('stationVolumes.panel.keepBandsHelp')}>
        <Checkbox
          data-testid={deviation ? 'dev-station-bands-on-map' : 'sd-station-bands-on-map'}
          checked={pinned}
          aria-disabled={gate.allowed ? undefined : true}
          data-locked={gate.allowed ? undefined : 'true'}
          onCheckedChange={(c) => {
            if (!gate.allowed) {
              gate.promptUpgrade();
              return;
            }
            // Pinning means "show them": a bands toggle switched off in the
            // dock some time ago must not leave the pin drawing nothing.
            if (c) setBandsVisible(true);
            setPinnedLayerId(c ? layerId : null);
          }}
        />
        {t('stationVolumes.panel.showBandsOnMap')}
        {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
      </label>
      {pinned && <PinnedBandsKey layerId={layerId} />}
    </>
  );
};

/**
 * The legend (and the stale-alignment warning) under the toggle, only while
 * the PIN is what draws this run's bands: with the dock open on it, the dock
 * header carries both and saying it twice on screen is noise.
 *
 * A component of its own so that only a pinned chip subscribes to the band
 * source — every other chip on screen fetches and re-renders nothing.
 */
const PinnedBandsKey = ({ layerId }: { layerId: string }) => {
  const { t } = useTranslation('project');
  const source = useBandSource();
  if (!source.isPinned || source.layerId !== layerId || !source.json || source.json.bins.length === 0) return null;
  const { bins } = source.json;
  return (
    <div className='mt-1.5 flex flex-col gap-1 [&>[data-testid=station-bands-legend]]:justify-start'>
      {!source.alignmentMatches && (
        <p
          data-testid='station-bands-on-map-stale'
          className='text-[11px] leading-snug text-amber-700 dark:text-amber-400'
          title={t('stationVolumes.panel.alignmentChangedHelp')}>
          {t('stationVolumes.panel.alignmentChanged')}
        </p>
      )}
      <StationBandsLegend bins={bins} lowCoverage={bins.some((b) => b.lowCoverage)} />
    </div>
  );
};
